import { Form } from "@unform/web";
import {
  Download,
  Contact,
  CalendarDays,
  Files,
  Mail,
  Send,
  Ticket,
  TicketsPlane,
  X,
} from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { Zoom, toast } from "react-toastify";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import FindGeneric from "~/components/Finds/FindGeneric";
import Input from "~/components/RegisterForm/Input";
import DatePicker from "~/components/RegisterForm/DatePicker";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { preArrivalsStatusOptions } from "~/functions/selectPopoverOptions";
import FileInputMultiple from "~/components/RegisterForm/FileInputMultiple";
import FileInput from "~/components/RegisterForm/FileInput";
import { organizeMultiAndSingleFiles } from "~/functions/uploadFile";
import { getDownloadURL, getStorage, ref, uploadBytes } from "firebase/storage";
import { app } from "~/services/firebase";
import { format, parseISO, isValid } from "date-fns";
import { Scope } from "@unform/core";
import { AlertContext } from "~/App";
import NewDatepicker from "~/pages/Financial/Outbounds/Payees/ModalFilters/NewDatepicker";
import { useField } from "@unform/core";
import Textarea from "~/components/RegisterForm/Textarea";

export const InputContext = createContext({});

const STATUSES_REQUIRING_INACTIVATION = ["Gave Up", "Denied"];

function InactivationDateField({ name, title, required = false, grow = false, defaultValue = "", InputContext }) {
  const { fieldName, registerField } = useField(name);
  const { setSuccessfullyUpdated } = useContext(InputContext);
  const [value, setValue] = useState(defaultValue || "");
  const fieldRef = useRef({ value: defaultValue || "" });

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: fieldRef.current,
      path: "value",
      setValue: (ref, nextValue) => { ref.value = nextValue || ""; setValue(nextValue || ""); },
      clearValue: (ref) => { ref.value = ""; setValue(""); },
    });
  }, [fieldName, registerField]);

  return (
    <div className={`flex flex-col justify-center items-start ${grow ? "grow" : ""}`}>
      <div className="px-2 text-xs flex flex-row justify-between items-center">
        {title} {required && <span className="text-red-600">*</span>}
      </div>
      <div className="w-full border border-gray-300 rounded-xs px-2 py-1 text-sm">
        <NewDatepicker
          value={value}
          onChange={(v) => { fieldRef.current.value = v || ""; setValue(v || ""); setSuccessfullyUpdated(false); }}
          className="w-full"
          placeholder="MM/DD/YYYY"
        />
      </div>
    </div>
  );
}

const safeParseDate = (value) => {
  if (!value || typeof value !== "string") {
    console.log("safeParseDate skip", { value });
    return null;
  }

  const iso = value.includes("T") ? value : value.includes("-") && !value.includes("/") ? value : value.replace(" ", "T");
  const parsed = parseISO(iso);
  const isValid = !Number.isNaN(parsed.getTime());
  console.log("safeParseDate parse", { value, iso, isValid });

  return isValid ? parsed : null;
};

const formatUpdatedAtTitle = (value) => {
  if (!value) return "";
  const iso = value.replace(" ", "T").replace("+00", "Z");
  const parsed = parseISO(iso);
  const isValid = !Number.isNaN(parsed.getTime());
  console.log("updated_at parse", { value, iso, isValid });
  if (!isValid) {
    return "";
  }
  return format(parsed, "MMMM do, yyyy");
};

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [loading, setLoading] = useState(false);
  const [pageData, setPageData] = useState({
    loaded: false,
    student: {
      id: null,
      name: "",
      last_name: "",
    },
    processes: [],
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [altExpectedDate, setAltExpectedDate] = useState(null);
  const [altExpectedMemo, setAltExpectedMemo] = useState("");
  const [expectedDateLogs, setExpectedDateLogs] = useState([]);
  const [loadingExpectedDateLogs, setLoadingExpectedDateLogs] = useState(false);
  const [altProgramStartDate, setAltProgramStartDate] = useState("");
  const [altProgramEndDate, setAltProgramEndDate] = useState("");
  const [altProgramMemo, setAltProgramMemo] = useState("");
  const [altProgramMode, setAltProgramMode] = useState(null);
  const [altProgramFileDeleted, setAltProgramFileDeleted] = useState(false);
  const [programChangeLogs, setProgramChangeLogs] = useState([]);
  const [loadingProgramChangeLogs, setLoadingProgramChangeLogs] =
    useState(false);
  const [documents, setDocuments] = useState([]);
  const [deleteFiles, setDeleteFiles] = useState([]);
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const { alertBox } = useContext(AlertContext);
  const generalForm = useRef();

  // Student inactivation tab state
  const [showInactivationTab, setShowInactivationTab] = useState(false);
  const [inactivationLocked, setInactivationLocked] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [reasonsData, setReasonsData] = useState([]);
  const [inactSelectedReason, setInactSelectedReason] = useState(null);
  const [inactSelectedDescription, setInactSelectedDescription] = useState(null);
  const inactivationFormRef = useRef({
    date: "", reason_id: null, description_id: null, option_id: null, memo: "",
  });

  const getChildAccess = (alias) =>
    access?.children?.find((child) => child.alias === alias) || {};
  const expectedDateAccess = getChildAccess("update-student-expected-date");
  const programStartDateAccess = getChildAccess("update-program-start-date");
  const canViewExpectedDate =
    expectedDateAccess?.MenuHierarchyXGroup?.view ??
    expectedDateAccess?.view ??
    true;
  const canEditExpectedDate =
    expectedDateAccess?.MenuHierarchyXGroup?.edit ??
    expectedDateAccess?.edit ??
    true;
  const canViewProgramStartDate =
    programStartDateAccess?.MenuHierarchyXGroup?.view ??
    programStartDateAccess?.view ??
    true;
  const canEditProgramStartDate =
    programStartDateAccess?.MenuHierarchyXGroup?.edit ??
    programStartDateAccess?.edit ??
    true;

  async function getPageData() {
    if (id !== "new") {
      try {
        const { data } = await api.get(`/pre-arrivals/${id}`);
        const { data: dataDocuments } = await api.get(
          `/documents?type=Pre-Arrivals`
        );
        setDocuments(dataDocuments?.rows);

        const { data: dataStudent } = await api.get(
          `/students/${data.student_id}`
        );

        const { enrollment_id } = dataStudent?.contracts[0];

        let dataEnrollment = null;
        if (enrollment_id) {
          const { data } = await api.get(`/enrollments/${enrollment_id}`);
          dataEnrollment = data;
        }

        setPageData({
          ...data,
          student: dataStudent,
          enrollment: dataEnrollment,
          loaded: true,
        });
        setCurrentStatus(data?.processes?.[0]?.status || "Processing");

        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        console.log(err);
        toast(err.response.data.error, { type: "error", autoClose: 3000 });
      }
    } else {
      setPageData({
        ...pageData,
        loaded: true,
      });
    }
  }

  useEffect(() => {
    getPageData();
  }, []);

  useEffect(() => {
    const program = pageData?.student?.programs?.[0];
    if (!program) return;
    if (!altProgramStartDate && program.start_date) {
      setAltProgramStartDate(program.start_date);
    }
    if (!altProgramEndDate && program.end_date) {
      setAltProgramEndDate(program.end_date);
    }
  }, [pageData?.student?.programs?.[0]?.id]);

  useEffect(() => {
    if (altExpectedDate !== null) return;
    const expected = pageData?.student?.expected_start_date;
    if (expected) {
      setAltExpectedDate(safeParseDate(expected));
    }
  }, [pageData?.student?.expected_start_date, altExpectedDate]);

  useEffect(() => {
    if (
      activeMenu === "update-student-expected-date" &&
      !canViewExpectedDate
    ) {
      setActiveMenu("general");
    }
    if (
      activeMenu === "update-program-start-date" &&
      !canViewProgramStartDate
    ) {
      setActiveMenu("general");
    }
  }, [activeMenu, canViewExpectedDate, canViewProgramStartDate]);

  const fetchExpectedDateLogs = async (studentId) => {
    if (!studentId) return;
    setLoadingExpectedDateLogs(true);
    try {
      const { data } = await api.get(
        `/students/${studentId}/expected-date-logs`
      );
      setExpectedDateLogs(data?.rows || []);
    } catch {
      setExpectedDateLogs([]);
    } finally {
      setLoadingExpectedDateLogs(false);
    }
  };

  useEffect(() => {
    const studentId = pageData?.student?.id;
    if (!studentId || activeMenu !== "update-student-expected-date") return;
    fetchExpectedDateLogs(studentId);
  }, [activeMenu, pageData?.student?.id]);

  const fetchProgramChangeLogs = async (studentId) => {
    if (!studentId) return;
    setLoadingProgramChangeLogs(true);
    try {
      const { data } = await api.get(
        `/students/${studentId}/studentprogram-change-logs`
      );
      setProgramChangeLogs(data?.rows || []);
    } catch {
      setProgramChangeLogs([]);
    } finally {
      setLoadingProgramChangeLogs(false);
    }
  };

  useEffect(() => {
    const studentId = pageData?.student?.id;
    if (!studentId || activeMenu !== "update-program-start-date") return;
    fetchProgramChangeLogs(studentId);
  }, [activeMenu, pageData?.student?.id]);

  useEffect(() => {
    async function loadReasons() {
      try {
        const { data } = await api.get(`/inactivation-reasons/options`);
        setReasonsData(data || []);
      } catch (err) {
        console.error("Failed to load inactivation reasons", err);
      }
    }
    loadReasons();
  }, []);

  async function handleChangeExpectedDate() {
    const studentId = pageData?.student?.id;
    if (!studentId) {
      toast("Student not found.", { type: "error", autoClose: 2000 });
      return;
    }
    if (!altExpectedMemo || altExpectedMemo.trim() === "") {
      toast("Comment is required.", { type: "error", autoClose: 2000 });
      return;
    }
    setLoading(true);
    try {
      await api.post(`/students/${studentId}/change-expected-date`, {
        expected_start_date: format(altExpectedDate, "yyyy-MM-dd"),
        memo: altExpectedMemo,
      });
      await fetchExpectedDateLogs(studentId);
      toast("Expected date updated.", {
        type: "success",
        autoClose: 2000,
      });
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to update expected date.";
      toast(message, { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  async function handleChangeProgram() {
    const programId = pageData?.student?.programs?.[0]?.id;
    const studentId = pageData?.student?.id;
    if (!programId) {
      toast("Student program not found.", { type: "error", autoClose: 2000 });
      return;
    }
    if (!altProgramMode) return;
    if (!altProgramMemo || altProgramMemo.trim() === "") {
      toast("Comment is required.", { type: "error", autoClose: 2000 });
      return;
    }

    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Do you want to update the expected start date for this student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: async () => {
            await executeChangeProgram(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await executeChangeProgram(true);
          },
        },
      ],
    });
  }

  async function executeChangeProgram(updateExpectedStartDate) {
    const programId = pageData?.student?.programs?.[0]?.id;
    const studentId = pageData?.student?.id;

    setLoading(true);
    try {
      const fileField = generalForm?.current?.getFieldValue("alt_program_file");
      let filePayload = null;

      if (fileField) {
        const [result] = await Promise.all(
          organizeMultiAndSingleFiles(
            [{ file_id: fileField, document_id: null }],
            "StudentPrograms"
          )
        );
        const resolved = Array.isArray(result) ? result[0] : result;
        if (resolved) {
          const { document_id, ...rest } = resolved;
          filePayload = rest;
        }
      }

      const payload = {
        start_date: altProgramStartDate || null,
        end_date: altProgramEndDate || null,
        file_id: filePayload,
        memo: altProgramMemo,
        type: altProgramMode,
        update_expected_start_date: updateExpectedStartDate,
      };
      if (altProgramMode === "add-new") {
        payload.student_id = pageData?.student?.id || null;
      }
      console.log("[Alt Start Date] change-program payload:", payload);
      await api.post(`/studentprogram/${programId}/change-program`, payload);

      await fetchProgramChangeLogs(studentId);

      const { data: updatedStudent } = await api.get(`/students/${studentId}`);

      setPageData((prev) => {
        const programs = prev?.student?.programs || [];
        const updatedPrograms = programs.map((program) => {
          if (program.id !== programId) return program;
          return {
            ...program,
            start_date: altProgramStartDate || program.start_date,
            end_date: altProgramEndDate || program.end_date,
            i20: filePayload ? { ...filePayload } : program.i20,
          };
        });
        return {
          ...prev,
          student: {
            ...prev.student,
            ...updatedStudent,
            programs: updatedPrograms,
          },
        };
      });

      if (updatedStudent?.expected_start_date) {
        setAltExpectedDate(safeParseDate(updatedStudent.expected_start_date));
      }

      setAltProgramStartDate(altProgramStartDate || "");
      setAltProgramEndDate(altProgramEndDate || "");
      setAltProgramMode(null);
      setAltProgramMemo("");
      setAltProgramFileDeleted(false);
      if (generalForm?.current?.setFieldValue) {
        generalForm.current.setFieldValue("alt_program_file", null);
      }
      toast("Program updated.", {
        type: "success",
        autoClose: 2000,
      });
    } catch (err) {
      const message =
        err?.response?.data?.error || "Failed to update program.";
      toast(message, { type: "error", autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  }

  async function handleGeneralFormSubmit(data) {
    const uploadedFiles = [];
    setLoading(true);
    try {
      if (data.files) {
        for (const fileLine of data.files) {
          if (!fileLine || !fileLine.files || fileLine.files.length === 0)
            continue;
          if (fileLine.files.length > 0) {
            for (const file of fileLine.files) {
              await uploadFile(file, fileLine.document_id);
            }
          } else {
            await uploadFile(fileLine.files, fileLine.document_id);
          }
        }
        data.files = uploadedFiles;
      }
      if (data.notice_date) {
        data.notice_date = format(data.notice_date, "yyyy-MM-dd");
      }
      if (deleteFiles.length > 0) {
        data.deleteFiles = deleteFiles;
      }
      if (successfullyUpdated) {
        toast("No need to be saved!", {
          autoClose: 1000,
          type: "info",
          transition: Zoom,
        });
        return;
      }

      if (data.inact_date) inactivationFormRef.current.date = data.inact_date;
      if (data.inact_memo) inactivationFormRef.current.memo = data.inact_memo;

      const selectedStatus = currentStatus ?? data.status;
      data.status = selectedStatus;
      const needsInactivation = STATUSES_REQUIRING_INACTIVATION.includes(selectedStatus);

      if (needsInactivation && !showInactivationTab) {
        setLoading(false);
        alertBox({
          title: "Student Inactivation Required",
          descriptionHTML: `To proceed with status <strong>${selectedStatus}</strong>, the student must be inactivated. Please fill in the <strong>Student Inactivation</strong> tab and save again.`,
          buttons: [
            {
              title: "OK",
              onPress: () => {
                setShowInactivationTab(true);
                setInactivationLocked(true);
                setActiveMenu("student-inactivation");
              },
            },
          ],
        });
        return;
      }

      if (needsInactivation && showInactivationTab) {
        const inactDate = inactivationFormRef.current.date;
        const inactReasonId = inactivationFormRef.current.reason_id;

        if (!inactDate) {
          toast("Please fill in the inactivation date.", { type: "error", autoClose: 3000 });
          setActiveMenu("student-inactivation");
          setLoading(false);
          return;
        }
        if (!inactReasonId) {
          toast("Please select an inactivation reason.", { type: "error", autoClose: 3000 });
          setActiveMenu("student-inactivation");
          setLoading(false);
          return;
        }

        let normalizedDate = inactDate;
        if (/^\d{2}\/\d{2}\/\d{4}$/.test(normalizedDate)) {
          const [mm, dd, yyyy] = normalizedDate.split("/");
          normalizedDate = `${yyyy}-${mm}-${dd}`;
        }

        await api.post(`/students/inactivate`, {
          student_id: pageData.student.id,
          date: normalizedDate,
          reason: inactSelectedReason?.name,
          reason_description: inactSelectedDescription?.name || null,
          inactivation_reason_id: inactSelectedReason?.id,
          inactivation_reason_description_id: inactSelectedDescription?.id || null,
          inactivation_reason_description_option_id: inactivationFormRef.current.option_id || null,
          memo: inactivationFormRef.current.memo || null,
        });
      }

      await api
        .put(`/pre-arrivals/${id}`, data)
        .catch((err) => {
          toast(err.response.data.error, {
            type: "error",
            autoClose: 3000,
          });
        })
        .then(() => {
          toast("Pre-Arrival updated successfully!", {
            type: "success",
            autoClose: 2000,
          });
          setSuccessfullyUpdated(true);
          handleOpened(null);
        });
    } catch (err) {
      console.log(err);
      // toast(err.response.data.error, {
      //   type: "error",
      //   autoClose: 3000,
      // });
    } finally {
      setLoading(false);
    }
    async function uploadFile(file, document_id) {
      const { name, size } = file;
      const fileUuid = uuidv4();
      const storage = getStorage(app);
      const local = "PreArrivals/Files/" + fileUuid + ".png";
      const imageRef = ref(storage, local);
      await uploadBytes(imageRef, file).then(async (snapshot) => {
        await getDownloadURL(snapshot.ref).then(async (downloadURL) => {
          file.downloadURL = downloadURL;
          uploadedFiles.push({
            name,
            size,
            downloadURL,
            key: fileUuid + ".png",
            document_id,
          });
        });
      });
    }
  }

  async function handleSendPlacementTestLink(enrollment_id) {
    setLoading(true);
    alertBox({
      title: "Attention!",
      descriptionHTML:
        "Are you sure you want to send the placement test link to the student?",
      buttons: [
        {
          title: "No",
          class: "cancel",
          onPress: () => {
            setLoading(false);
          },
        },
        {
          title: "Yes",
          onPress: async () => {
            await api
              .post(`/enrollments/start-placement-test/`, {
                enrollment_id,
              })
              .catch((err) => {
                toast(err.response.data.error, {
                  type: "error",
                  autoClose: 3000,
                });
              })
              .then(() => {
                toast("E-mail sent to the student!", {
                  autoClose: 1000,
                });
              })
              .finally(() => {
                setLoading(false);
              });
          },
        },
      ],
    });
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleDeleteFile(id) {
    setDeleteFiles([...deleteFiles, id]);
    setSuccessfullyUpdated(false);
    setPageData({
      ...pageData,
      files: pageData.files.filter((file) => file.id !== id),
    });
  }

  const isTransfer = pageData.contract?.processsubstatuses?.name === "Transfer";
  const isInitial =
    pageData.contract?.processsubstatuses?.name === "Initial Visa";
  const isCOS =
    pageData.contract?.processsubstatuses?.name === "Change of Status";
  const isReinstatement =
    pageData.contract?.processsubstatuses?.name === "Reinstatement";

  const process = pageData?.processes[0];

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <TicketsPlane size={16} /> General
            </RegisterFormMenu>
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="processes"
              disabled={inactivationLocked}
            >
              <Ticket size={16} /> Last Processes
            </RegisterFormMenu>
            {canViewExpectedDate && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="update-student-expected-date"
                disabled={inactivationLocked}
              >
                <CalendarDays size={16} /> Update Student Expected Date
              </RegisterFormMenu>
            )}
            {canViewProgramStartDate && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="update-program-start-date"
                disabled={inactivationLocked}
              >
                <CalendarDays size={16} /> Update Program Start Date
              </RegisterFormMenu>
            )}
            {showInactivationTab && (
              <RegisterFormMenu
                setActiveMenu={setActiveMenu}
                activeMenu={activeMenu}
                name="student-inactivation"
              >
                <span className="text-red-500 font-bold">⚠</span> Student Inactivation
              </RegisterFormMenu>
            )}
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <InputContext.Provider
              value={{
                id,
                generalForm,
                setSuccessfullyUpdated,
                fullscreen,
                setFullscreen,
                successfullyUpdated,
                handleCloseForm,
                canceled: pageData.canceled_at,
              }}
            >
              <div
                id="scrollPage"
                className="flex flex-col items-start justify-start text-sm overflow-y-scroll"
              >
                <Form
                  ref={generalForm}
                  onSubmit={handleGeneralFormSubmit}
                  className="w-full"
                >
                  <FormHeader
                    access={access}
                    loading={loading}
                    title={
                      pageData.student.name + " " + pageData.student.last_name
                    }
                    registry={registry}
                    InputContext={InputContext}
                  />
                  {id === "new" || pageData.loaded ? (
                    <>
                      {activeMenu === "general" && (
                        <InputLineGroup
                          title="GENERAL"
                          activeMenu={activeMenu === "general"}
                        >
                          <FindGeneric
                            name="processtypes"
                            title="Process Type"
                            scope="processtypes"
                            readOnly
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.contract?.processtypes?.id,
                              name: pageData.contract?.processtypes?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <FindGeneric
                            name="processsubstatuses"
                            title="Process Sub Status"
                            scope="processsubstatuses"
                            readOnly
                            InputContext={InputContext}
                            defaultValue={{
                              id: pageData.contract?.processsubstatuses?.id,
                              name: pageData.contract?.processsubstatuses?.name,
                            }}
                            fields={[
                              {
                                title: "Name",
                                name: "name",
                              },
                            ]}
                          />
                          <InputLine title="Placement Test">
                            <Input
                              type="text"
                              name="placement_test_status"
                              readOnly
                              grow
                              title="Status"
                              defaultValue={
                                pageData?.enrollment?.placement_test_status ||
                                "Not started"
                              }
                              InputContext={InputContext}
                            />
                            {pageData?.enrollment?.placement_test_status.toUpperCase() ===
                              "NOT STARTED" ||
                            !pageData?.enrollment?.placement_test_status ? (
                              <>
                                {pageData?.enrollment?.id && (
                                  <button
                                    type="button"
                                    className="px-4 py-2 rounded-md mt-4 flex flex-row items-center justify-center text-sm border border-red-500 text-red-500 gap-1 hover:border-primary hover:text-primary cursor-pointer"
                                    onClick={() =>
                                      handleSendPlacementTestLink(
                                        pageData?.enrollment?.id
                                      )
                                    }
                                  >
                                    Resend placement test link
                                    <Send size={14} />
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <Input
                                  type="text"
                                  name="placement_test_score"
                                  readOnly
                                  grow
                                  title="Placement Test Score"
                                  defaultValue={
                                    pageData?.enrollment?.placement_test_score
                                  }
                                  InputContext={InputContext}
                                />
                                <Input
                                  type="text"
                                  name="final_level"
                                  readOnly
                                  grow
                                  title="Final Level"
                                  defaultValue={
                                    pageData?.enrollment?.final_level
                                  }
                                  InputContext={InputContext}
                                />
                                <DatePicker
                                  name="appointment_date"
                                  grow
                                  readOnly
                                  title="Appointment Date"
                                defaultValue={
                                  pageData?.enrollment?.appointment_date
                                    ? safeParseDate(
                                        pageData?.enrollment?.appointment_date
                                      )
                                    : null
                                }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                />
                              </>
                            )}
                          </InputLine>
                          <InputLine title="Student's Plan">
                            <DatePicker
                              name="plan_date"
                              grow
                              readOnly
                              title="Start Date"
                              defaultValue={
                                pageData?.student?.start_date
                                  ? safeParseDate(pageData?.student?.start_date)
                                  : pageData?.enrollment?.plan_date
                                  ? safeParseDate(pageData?.enrollment?.plan_date)
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="plan_schedule"
                              grow
                              readOnly
                              title="Schedule"
                              defaultValue={pageData?.enrollment?.plan_schedule}
                              InputContext={InputContext}
                            />
                            <Input
                              type="text"
                              name="plan_months"
                              grow
                              readOnly
                              title="Months"
                              defaultValue={pageData?.enrollment?.plan_months}
                              InputContext={InputContext}
                            />
                            <DatePicker
                              name="expected_start_date"
                              grow
                              readOnly
                              title="Expected Start Date"
                              defaultValue={
                                pageData?.student?.expected_start_date
                                  ? safeParseDate(
                                      pageData?.student?.expected_start_date
                                    )
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="I-20 Details">
                            <DatePicker
                              name="program_start_date"
                              grow
                              readOnly
                              title="Program Start Date"
                              defaultValue={
                                (pageData.enrollment?.student?.programs[0] ||
                                  pageData.student?.programs[0])?.start_date
                                  ? safeParseDate(
                                      (pageData.enrollment?.student
                                        ?.programs[0] ||
                                        pageData.student?.programs[0])?.start_date
                                    )
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                            <DatePicker
                              name="program_end_date"
                              grow
                              readOnly
                              title="Program End Date"
                              defaultValue={
                                (pageData.enrollment?.student?.programs[0] ||
                                  pageData.student?.programs[0])?.end_date
                                  ? safeParseDate(
                                      (pageData.enrollment?.student
                                        ?.programs[0] ||
                                        pageData.student?.programs[0])?.end_date
                                    )
                                  : null
                              }
                              placeholderText="MM/DD/YYYY"
                              InputContext={InputContext}
                            />
                          </InputLine>
                          <InputLine title="General Data">
                            {isInitial && (
                              <DatePicker
                                name="interview_date"
                                grow
                                title="Interview Date"
                                defaultValue={
                                  process?.interview_date
                                    ? parseISO(process?.interview_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2"))
                                    : null
                                }
                                placeholderText="MM/DD/YYYY"
                                InputContext={InputContext}
                              />
                            )}
                            {isCOS && (
                              <>
                                <Input
                                  type="text"
                                  name="case_number"
                                  grow
                                  title="Case Number"
                                  defaultValue={process?.case_number}
                                  InputContext={InputContext}
                                  readOnly={inactivationLocked}
                                />
                                <DatePicker
                                  name="notice_date"
                                  grow
                                  title="Notice Date"
                                  defaultValue={
                                    process?.notice_date
                                      ? parseISO(process?.notice_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2"))
                                      : null
                                  }
                                  placeholderText="MM/DD/YYYY"
                                  InputContext={InputContext}
                                  readOnly={inactivationLocked}
                                />
                              </>
                            )}
                            <SelectPopover
                              name="status"
                              grow
                              title="Status"
                              options={preArrivalsStatusOptions}
                              isSearchable
                              defaultValue={preArrivalsStatusOptions.find(
                                (opt) =>
                                  opt.value ===
                                  (process?.status || "Processing")
                              )}
                              InputContext={InputContext}
                              readOnly={inactivationLocked}
                              onChange={(opt) => setCurrentStatus(opt?.value || null)}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                            />
                          </InputLine>
                          {documents?.length > 0 &&
                            documents.map((document, index) => {
                              if (
                                document.type !==
                                pageData.contract?.processsubstatuses?.name
                              ) {
                                return null;
                              }
                              return (
                                <Scope key={index} path={`files[${index}]`}>
                                  <InputLine title={document.title}>
                                    <Input
                                      type="hidden"
                                      name="document_id"
                                      defaultValue={document.id}
                                      InputContext={InputContext}
                                    />
                                    {document.multiple ? (
                                      <FileInputMultiple
                                        key={document.id}
                                        name="files"
                                        title="Upload Files"
                                        grow
                                        required={
                                          document.required &&
                                          pageData.files.length === 0
                                        }
                                        multiple={document.multiple}
                                        InputContext={InputContext}
                                      />
                                    ) : (
                                      <FileInput
                                        key={document.id}
                                        name="files"
                                        title="Upload File"
                                        grow
                                        required={
                                          document.required &&
                                          pageData.files.length === 0
                                        }
                                        InputContext={InputContext}
                                      />
                                    )}
                                  </InputLine>
                                  <InputLine>
                                    {pageData.files &&
                                      pageData.files.length > 0 && (
                                        <div className="w-full flex flex-row items-center justify-start flex-wrap gap-2">
                                          {pageData.files
                                            .filter(
                                              (file) =>
                                                !deleteFiles.includes(file.id)
                                            )
                                            .filter(
                                              (file) =>
                                                file.document_id === document.id
                                            )
                                            .map((file, index) => (
                                              <div
                                                key={index}
                                                className="flex flex-row items-center justify-start border rounded-md  px-2 gap-1 cursor-normal"
                                              >
                                                <button
                                                  type="button"
                                                  onClick={() =>
                                                    handleDeleteFile(file.id)
                                                  }
                                                  className="text-xs text-red-700 cursor-pointer flex flex-row items-center justify-start gap-1 px-1 mx-1 py-1 rounded-sm hover:bg-red-100"
                                                >
                                                  <X size={12} />{" "}
                                                </button>
                                                <a
                                                  href={file.file.url}
                                                  target="_blank"
                                                  className="text-xs w-full sm:w-auto"
                                                >
                                                  <div className="flex flex-row items-center py-2 gap-1 truncate">
                                                    <Files
                                                      size={16}
                                                      className="flex-shrink-0"
                                                    />
                                                    <span className="truncate">
                                                      {file.file.name}
                                                    </span>
                                                  </div>
                                                </a>
                                              </div>
                                            ))}
                                        </div>
                                      )}
                                  </InputLine>
                                </Scope>
                              );
                            })}
                        </InputLineGroup>
                      )}
                      {activeMenu === "processes" && (
                        <InputLineGroup
                          title="PROCESSES"
                          activeMenu={activeMenu === "processes"}
                        >
                          {pageData?.processes?.length > 0 &&
                            pageData?.processes?.map((process, index) => {
                              return (
                                <InputLine
                                  key={index}
                                  title={formatUpdatedAtTitle(
                                    process.updated_at
                                  )}
                                >
                                  {isInitial && (
                                    <DatePicker
                                      name="interview_date"
                                      grow
                                      readOnly
                                      title="Interview Date"
                                      defaultValue={
                                        process?.interview_date
                                          ? safeParseDate(
                                              process?.interview_date
                                            )
                                          : null
                                      }
                                      placeholderText="MM/DD/YYYY"
                                      InputContext={InputContext}
                                    />
                                  )}
                                  {isCOS && (
                                    <>
                                      <Input
                                        type="text"
                                        name="case_number"
                                        grow
                                        readOnly
                                        title="Case Number"
                                        defaultValue={process?.case_number}
                                        InputContext={InputContext}
                                      />
                                      <DatePicker
                                        name="notice_date"
                                        grow
                                        readOnly
                                        title="Notice Date"
                                      defaultValue={
                                        process?.notice_date
                                          ? parseISO(process?.notice_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2"))
                                          : null
                                      }
                                        placeholderText="MM/DD/YYYY"
                                        InputContext={InputContext}
                                      />
                                    </>
                                  )}
                                  <SelectPopover
                                    name="status"
                                    grow
                                    readOnly
                                    title="Status"
                                    options={preArrivalsStatusOptions}
                                    isSearchable
                                    defaultValue={preArrivalsStatusOptions.find(
                                      (opt) =>
                                        opt.value ===
                                        (process?.status || "Processing")
                                    )}
                                    InputContext={InputContext}
                                  />
                                </InputLine>
                              );
                            })}
                        </InputLineGroup>
                      )}
                      {activeMenu === "update-student-expected-date" &&
                        canViewExpectedDate && (
                        <InputLineGroup
                          title="UPDATE STUDENT EXPECTED DATE"
                          activeMenu={activeMenu === "update-student-expected-date"}
                        >
                          <InputLine title="Update Student Expected Date">
                            <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                              <div className="px-2 text-xs flex flex-row justify-between items-center">
                                Current Date
                              </div>
                              <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                <input
                                  type="date"
                                  className="bg-transparent w-full"
                                  value={
                                    altExpectedDate
                                      ? format(altExpectedDate, "yyyy-MM-dd")
                                      : ""
                                  }
                                  disabled={!canEditExpectedDate}
                                  onChange={(e) => {
                                    const next = e.target.value
                                      ? new Date(`${e.target.value}T00:00:00`)
                                      : null;
                                    setAltExpectedDate(next);
                                  }}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                              <div className="px-2 text-xs flex flex-row justify-between items-center">
                                Comment
                              </div>
                              <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                <input
                                  type="text"
                                  className="bg-transparent w-full"
                                  value={altExpectedMemo}
                                  onChange={(e) =>
                                    setAltExpectedMemo(e.target.value)
                                  }
                                  placeholder="Add a note..."
                                  disabled={!canEditExpectedDate}
                                />
                              </div>
                            </div>
                            <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                              <div className="px-2 text-xs flex flex-row justify-between items-center">
                                &nbsp;
                              </div>
                              <button
                                type="button"
                                onClick={handleChangeExpectedDate}
                                disabled={loading || !canEditExpectedDate}
                                className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm"
                              >
                                Confirm
                              </button>
                            </div>
                          </InputLine>
                          {!loadingExpectedDateLogs &&
                            expectedDateLogs.length > 0 && (
                              <div className="w-full px-2 pb-4 mt-4">
                                <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                                  History
                                </div>
                                <div className="w-full border rounded-md">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-secondary">
                                      <tr>
                                        <th className="px-3 py-2">
                                          Old Expected Start Date
                                        </th>
                                        <th className="px-3 py-2">
                                          New Expected Start Date
                                        </th>
                                        <th className="px-3 py-2">Comment</th>
                                        <th className="px-3 py-2">
                                          Modified At
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {expectedDateLogs.map((row) => (
                                        <tr
                                          key={row.id}
                                          className="odd:bg-white even:bg-gray-50"
                                        >
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_expected_start_date
                                              ? format(
                                                  parseISO(
                                                    row.old_expected_start_date
                                                  ),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.new_expected_start_date
                                              ? format(
                                                  parseISO(
                                                    row.new_expected_start_date
                                                  ),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2">
                                            {row.memo || "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.created_at
                                              ? format(
                                                  parseISO(row.created_at),
                                                  "MM/dd/yyyy HH:mm"
                                                )
                                              : "-"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                        </InputLineGroup>
                      )}
                      {activeMenu === "update-program-start-date" &&
                        canViewProgramStartDate && (
                        <InputLineGroup
                          title="UPDATE PROGRAM START DATE"
                          activeMenu={activeMenu === "update-program-start-date"}
                        >
                          <InputLine title="Update Program Start Date">
                            {!altProgramMode ? (
                              <>
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    I-20
                                  </div>
                                  {pageData?.student?.programs?.[0]?.i20?.url ? (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        window.open(
                                          pageData?.student?.programs?.[0]?.i20
                                            ?.url
                                        )
                                      }
                                      className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm flex flex-row items-center gap-2"
                                    >
                                      <Download size={14} />
                                      <span>Open I-20</span>
                                    </button>
                                  ) : (
                                    <div className="px-4 py-2 text-xs text-gray-500 border border-gray-300 rounded-sm bg-slate-100">
                                      No I-20 file
                                    </div>
                                  )}
                                </div>
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    Program Start Date
                                  </div>
                                  <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2 bg-slate-100">
                                    <input
                                      type="date"
                                      className="bg-transparent w-full"
                                      value={altProgramStartDate}
                                      readOnly
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    Program End Date
                                  </div>
                                  <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2 bg-slate-100">
                                    <input
                                      type="date"
                                      className="bg-transparent w-full"
                                      value={altProgramEndDate}
                                      readOnly
                                    />
                                  </div>
                                </div>
                              </>
                            ) : (
                              <>
                                {altProgramFileDeleted ||
                                altProgramMode === "add-new" ? (
                                  <FileInput
                                    name="alt_program_file"
                                    title="I-20"
                                    grow
                                    InputContext={InputContext}
                                    disabled={!canEditProgramStartDate}
                                  />
                                ) : (
                                  <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                                    <div className="px-2 text-xs flex flex-row justify-between items-center">
                                      I-20
                                    </div>
                                    <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row items-center justify-between gap-2 bg-slate-100">
                                      <span className="truncate">
                                        {pageData?.student?.programs?.[0]?.i20
                                          ?.name ||
                                          "Current I-20"}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setAltProgramFileDeleted(true);
                                          if (
                                            generalForm?.current?.setFieldValue
                                          ) {
                                            generalForm.current.setFieldValue(
                                              "alt_program_file",
                                              null
                                            );
                                          }
                                        }}
                                        className="text-xs text-red-700 hover:text-red-900"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                )}
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    Program Start Date
                                  </div>
                                  <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                    <input
                                      type="date"
                                      className="bg-transparent w-full"
                                      value={altProgramStartDate}
                                      disabled={!canEditProgramStartDate}
                                      onChange={(e) =>
                                        setAltProgramStartDate(e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    Program End Date
                                  </div>
                                  <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                    <input
                                      type="date"
                                      className="bg-transparent w-full"
                                      value={altProgramEndDate}
                                      disabled={!canEditProgramStartDate}
                                      onChange={(e) =>
                                        setAltProgramEndDate(e.target.value)
                                      }
                                    />
                                  </div>
                                </div>
                                <div className="flex flex-col justify-center items-start relative w-full md:w-auto grow">
                                  <div className="px-2 text-xs flex flex-row justify-between items-center">
                                    Comment
                                  </div>
                                  <div className="w-full border rounded-xs p-2 px-4 text-sm flex flex-row justify-between items-center gap-2">
                                    <input
                                      type="text"
                                      className="bg-transparent w-full"
                                      value={altProgramMemo}
                                      onChange={(e) =>
                                        setAltProgramMemo(e.target.value)
                                      }
                                      placeholder="Why change?"
                                      disabled={!canEditProgramStartDate}
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                            <div className="flex flex-col justify-center items-start relative w-full md:w-auto">
                              <div className="px-2 text-xs flex flex-row justify-between items-center">
                                &nbsp;
                              </div>
                              <div className="flex flex-row items-center gap-2">
                                {!altProgramMode ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const program =
                                          pageData?.student?.programs?.[0];
                                        setAltProgramMode("update");
                                        setAltProgramFileDeleted(false);
                                        setAltProgramMemo("");
                                        setAltProgramStartDate(
                                          program?.start_date
                                            ? format(
                                                parseISO(program.start_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                "yyyy-MM-dd"
                                              )
                                            : ""
                                        );
                                        setAltProgramEndDate(
                                          program?.end_date
                                            ? format(
                                                parseISO(program.end_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                                "yyyy-MM-dd"
                                              )
                                            : ""
                                        );
                                      }}
                                      disabled={loading || !canEditProgramStartDate}
                                      className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm"
                                    >
                                      Update
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAltProgramMode("add-new");
                                        setAltProgramFileDeleted(true);
                                        setAltProgramMemo("");
                                        setAltProgramStartDate("");
                                        setAltProgramEndDate("");
                                        if (
                                          generalForm?.current?.setFieldValue
                                        ) {
                                          generalForm.current.setFieldValue(
                                            "alt_program_file",
                                            null
                                          );
                                        }
                                      }}
                                      disabled={loading || !canEditProgramStartDate}
                                      className="px-4 py-2 text-xs font-bold border border-gray-300 text-gray-700 transition-all hover:bg-gray-100 rounded-sm"
                                    >
                                      Add New
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      type="button"
                                      onClick={handleChangeProgram}
                                      disabled={loading || !canEditProgramStartDate}
                                      className="px-4 py-2 text-xs font-bold border border-mila_orange text-mila_orange transition-all hover:bg-mila_orange hover:text-white rounded-sm"
                                    >
                                      Confirm
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setAltProgramMode(null);
                                        setAltProgramMemo("");
                                        setAltProgramFileDeleted(false);
                                        const program =
                                          pageData?.student?.programs?.[0];
                                        if (program?.start_date) {
                                          setAltProgramStartDate(
                                            format(
                                              parseISO(program.start_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                              "yyyy-MM-dd"
                                            )
                                          );
                                        } else {
                                          setAltProgramStartDate("");
                                        }
                                        if (program?.end_date) {
                                          setAltProgramEndDate(
                                            format(
                                              parseISO(program.end_date.replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$3-$1-$2")),
                                              "yyyy-MM-dd"
                                            )
                                          );
                                        } else {
                                          setAltProgramEndDate("");
                                        }
                                        if (
                                          generalForm?.current?.setFieldValue
                                        ) {
                                          generalForm.current.setFieldValue(
                                            "alt_program_file",
                                            null
                                          );
                                        }
                                      }}
                                      disabled={loading || !canEditProgramStartDate}
                                      className="px-4 py-2 text-xs font-bold border border-gray-300 text-gray-700 transition-all hover:bg-gray-100 rounded-sm"
                                    >
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </InputLine>
                          {!loadingProgramChangeLogs &&
                            programChangeLogs.length > 0 && (
                              <div className="w-full px-2 pb-4 mt-4">
                                <div className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">
                                  History
                                </div>
                                <div className="w-full border rounded-md">
                                  <table className="w-full text-xs text-left">
                                    <thead className="bg-secondary">
                                      <tr>
                                        <th className="px-3 py-2">
                                          Old Start Date
                                        </th>
                                        <th className="px-3 py-2">
                                          New Start Date
                                        </th>
                                        <th className="px-3 py-2">
                                          Old End Date
                                        </th>
                                        <th className="px-3 py-2">
                                          New End Date
                                        </th>
                                        <th className="px-3 py-2">Old File</th>
                                        <th className="px-3 py-2">Type</th>
                                        <th className="px-3 py-2">Comment</th>
                                        <th className="px-3 py-2">
                                          Created At
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {programChangeLogs.map((row) => (
                                        <tr
                                          key={row.id}
                                          className="odd:bg-white even:bg-gray-50"
                                        >
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_start_date
                                              ? format(
                                                  parseISO(row.old_start_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.new_start_date
                                              ? format(
                                                  parseISO(row.new_start_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_end_date
                                              ? format(
                                                  parseISO(row.old_end_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.new_end_date
                                              ? format(
                                                  parseISO(row.new_end_date),
                                                  "MM/dd/yyyy"
                                                )
                                              : "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.old_file?.url ? (
                                              <button
                                                type="button"
                                                onClick={() =>
                                                  window.open(
                                                    row.old_file.url
                                                  )
                                                }
                                                className="text-mila_orange hover:text-gray-700"
                                                title="Download old file"
                                              >
                                                <Files size={14} />
                                              </button>
                                            ) : (
                                              "-"
                                            )}
                                          </td>
                                          <td className="px-3 py-2">
                                            {row.type || "-"}
                                          </td>
                                          <td className="px-3 py-2">
                                            {row.memo || "-"}
                                          </td>
                                          <td className="px-3 py-2 whitespace-nowrap">
                                            {row.created_at
                                              ? format(
                                                  parseISO(row.created_at),
                                                  "MM/dd/yyyy HH:mm"
                                                )
                                              : "-"}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}
                        </InputLineGroup>
                      )}
                      {activeMenu === "student-inactivation" && showInactivationTab && (() => {
                        const descriptionOptions = inactSelectedReason
                          ? inactSelectedReason.descriptions.map((d) => ({ value: d.id, label: d.name, _description: d }))
                          : [];
                        const optionOptions = inactSelectedDescription && inactSelectedDescription.options?.length > 0
                          ? inactSelectedDescription.options.map((o) => ({ value: o.id, label: o.name }))
                          : [];
                        const reasonOptions = reasonsData.map((r) => ({ value: r.id, label: r.name, _reason: r }));
                        return (
                          <InputLineGroup
                            title="STUDENT INACTIVATION"
                            activeMenu={activeMenu === "student-inactivation"}
                          >
                            <InputLine title="Details">
                              <InactivationDateField
                                name="inact_date"
                                grow
                                required
                                title="Date"
                                defaultValue=""
                                InputContext={InputContext}
                              />
                              <SelectPopover
                                name="inact_reason_id"
                                grow
                                required
                                title="Reason"
                                isSearchable
                                options={reasonOptions}
                                InputContext={InputContext}
                                onChange={(el) => {
                                  setInactSelectedReason(el._reason);
                                  setInactSelectedDescription(null);
                                  inactivationFormRef.current.reason_id = el._reason.id;
                                  inactivationFormRef.current.description_id = null;
                                  inactivationFormRef.current.option_id = null;
                                  setSuccessfullyUpdated(false);
                                }}
                              />
                              {inactSelectedReason && descriptionOptions.length > 0 && (
                                <SelectPopover
                                  name="inact_description_id"
                                  grow
                                  required
                                  title="Description"
                                  isSearchable
                                  options={descriptionOptions}
                                  InputContext={InputContext}
                                  onChange={(el) => {
                                    setInactSelectedDescription(el._description);
                                    inactivationFormRef.current.description_id = el._description.id;
                                    inactivationFormRef.current.option_id = null;
                                    setSuccessfullyUpdated(false);
                                  }}
                                />
                              )}
                              {inactSelectedDescription && optionOptions.length > 0 && (
                                <SelectPopover
                                  name="inact_option_id"
                                  grow
                                  title="Option"
                                  isSearchable
                                  options={optionOptions}
                                  InputContext={InputContext}
                                  onChange={(el) => {
                                    inactivationFormRef.current.option_id = el.value;
                                    setSuccessfullyUpdated(false);
                                  }}
                                />
                              )}
                            </InputLine>
                            <InputLine title="Comment">
                              <Textarea
                                name="inact_memo"
                                title="Comment"
                                rows={4}
                                grow
                                defaultValue=""
                                InputContext={InputContext}
                                placeholder="Type a comment..."
                              />
                            </InputLine>
                          </InputLineGroup>
                        );
                      })()}
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </Form>
              </div>
            </InputContext.Provider>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
